import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  useTheme,
  Checkbox,
  ListItemText,
  ListSubheader,
} from "@mui/material";
import { useFormik, FormikErrors } from "formik";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import AlertConfirmationDialog from "components/ui/AlertConfirmationDialog";
import { ConfirmationDialog, SuccessMessage } from "components/ui";
import { AudienceCategory, IAlertApiPayload, IAlertFormData, SeverityType, SourceType } from "system/types/interfaces/IAlertFormData ";
import useManageAlertApi from "system/hooks/use-api-manage-alert";
import { sanitizeHtml } from "system/utils/sanitizeHtml";

interface CreateAlertProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: IAlertFormData) => void;
  editData?: IAlertFormData | null;
  isEditMode?: boolean;
  setRefreshAlerts: any;

  severityTypes: SeverityType[];
  audienceCategories: AudienceCategory[];
  sourceTypes: SourceType[];
}

interface ApiError {
  severityTypesError: string;
  audienceCategoryError: string;
  sourceTypesError: string;
}

interface LoadingState {
  severityTypes: boolean;
  audienceCategories: boolean;
  sourceTypes: boolean;
}

const INITIAL_VALUES: IAlertFormData = {
  alertTitle: "",
  severity: "",
  audience: [],
  status: "Draft",
  startDate: "",
  endDate: "",
  description: "",
};

const initialErrorState: ApiError = {
  severityTypesError: "",
  audienceCategoryError: "",
  sourceTypesError: "",
};

const initialLoadingState: LoadingState = {
  severityTypes: true,
  audienceCategories: true,
  sourceTypes: true,
};

const validateForm = (values: IAlertFormData): FormikErrors<IAlertFormData> => {
  const errors: FormikErrors<IAlertFormData> = {};
  const now = new Date();
  const start = values.startDate ? new Date(values.startDate) : null;
  const end = values.endDate ? new Date(values.endDate) : null;
  const isDisabling = values.status === "Disabled";

  if (!values.alertTitle?.trim()) {
    errors.alertTitle = "Required";
  } else if (values.alertTitle.trim().length < 3) {
    errors.alertTitle = "At least 3 characters";
  } else if (values.alertTitle.trim().length > 100) {
    errors.alertTitle = "Up to 100 characters";
  }

  if (!values.severity) errors.severity = "Required";
  if (values.audience.length === 0) errors.audience = "Select at least one audience";

  if (!isDisabling) {
    if (!values.startDate) errors.startDate = "Start Date is required";
    else if (start && start.getTime() < now.getTime()) errors.startDate = "Start Date cannot be in the past";

    if (!values.endDate) errors.endDate = "End Date is required";
    else if (start && end && end <= start) errors.endDate = "End Date must be after Start Date";
  }

  // DOMParser builds a separate, inert document, so unlike assigning to a live element's
  // innerHTML, no <img>/etc. in stored HTML loads and no event handlers (e.g. onerror) run.
  const stripHtml = (html: string) => new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";

  if (!values.description || stripHtml(values.description).trim() === "") {
    errors.description = "Description is required";
  }

  return errors;
};

const CreateAlert = ({
  open,
  onSave,
  onClose,
  editData,
  isEditMode = false,
  setRefreshAlerts,
  severityTypes,
  audienceCategories,
  sourceTypes,
}: CreateAlertProps) => {
  const theme = useTheme();
  const api = useManageAlertApi();

  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [successMessageTitle, setSuccessMessageTitle] = useState("");

  const [audienceSearch, setAudienceSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [originalStatus, setOriginalStatus] = useState<string | null>(null);

  // Sanitize a stored description before it ever reaches ReactQuill (which loads its initial
  // value via a live Quill 1.3.7 clipboard element) or stripHtml in validateForm below -
  // otherwise a stored payload like <img src=x onerror=...> executes as soon as the alert opens.
  const initialValues = useMemo(
    () => (editData ? { ...editData, description: sanitizeHtml(editData.description) } : INITIAL_VALUES),
    [editData]
  );

  const formik = useFormik<IAlertFormData>({
    initialValues,
    enableReinitialize: true,
    validate: validateForm,
    onSubmit: () => setConfirmationDialogVisible(true),
  });

  const filteredAudienceCategories = audienceCategories.filter((a) => a.name.toLowerCase().includes(audienceSearch.toLowerCase()));

  const formatToISO = (dateStr: string | null) => (dateStr ? new Date(dateStr).toISOString() : null);

  useEffect(() => {
    if (!open) return;

    if (!isEditMode && audienceCategories.length > 0) {
      const hasAllCustomers = audienceCategories.some((c) => c.name === "All Customers");
      if (hasAllCustomers && (!formik.values.audience || formik.values.audience.length === 0)) {
        formik.setFieldValue("audience", ["All Customers"]);
      }
    }
  }, [open, isEditMode, audienceCategories, formik]);

  const handleSaveAlert = useCallback(
    async (values: IAlertFormData) => {
      const severity = severityTypes.find((s) => s.name === values.severity);
      const source = sourceTypes.find((s) => s.name === values.status);

      const selectedNames = values.audience || [];
      const isAllCustomersSelected = selectedNames.includes("All Customers");

      let audienceIds: number[] = [];
      let audienceString = "";

      if (isAllCustomersSelected) {
        audienceIds = [];
        audienceString = "All Customers";
      } else {
        audienceIds = audienceCategories.filter((c) => selectedNames.includes(c.name)).map((c) => c.alertAudienceCategoryId);
        audienceString = selectedNames.join(", ");
      }

      const payload: IAlertApiPayload = {
        alertId: values.id ?? 0,
        subject: values.alertTitle,
        body: sanitizeHtml(values.description),
        status: source?.alertSourceTypeId ?? 0,
        severityTypeId: severity?.alertSeverityTypeId ?? 0,
        audienceCategory: audienceString,
        audienceCategoryId: audienceIds,
        sourceTypeId: source?.alertSourceTypeId ?? 0,
        applicabilityStartTime: formatToISO(values.startDate),
        applicabilityEndTime: formatToISO(values.endDate),
      };

      const response = isEditMode ? await api.updateAlert(payload) : await api.createAlert(payload);

      if (response?.isSuccess) {
        setRefreshAlerts(Math.random());
        onSave(values);
        setSuccessMessageTitle(isEditMode ? "Alert updated successfully" : "Alert created successfully");
        setSuccessMessageVisible(true);
        formik.resetForm();
        onClose();
      } else {
        alert(response?.failMessage || "Error saving alert");
      }
    },
    [severityTypes, sourceTypes, isEditMode, api, audienceCategories, setRefreshAlerts, onSave, formik, onClose]
  );

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await handleSaveAlert(formik.values);
    } finally {
      setIsSaving(false);
      setConfirmationDialogVisible(false);
    }
  };

  const getConfirmationMessage = () => {
    if (isEditMode && originalStatus && originalStatus !== formik.values.status) {
      return `Are you sure you want to change the status from "${originalStatus}" to "${formik.values.status}"?\nThis will notify the audience.`;
    }
    return isEditMode ? "Are you sure you want to update this alert?" : "Are you sure you want to publish this alert?";
  };

  const dialogStyles = {
    title: { backgroundColor: theme.palette.grey[900], color: "#fff" },
    content: { backgroundColor: theme.palette.grey[900], color: "#fff", pt: 3 },
    actions: { backgroundColor: theme.palette.grey[900], px: 3, py: 2 },
    inputLabel: { color: "#ccc" },
    input: { color: "#fff" },
  };

  const allNames = audienceCategories.map((c) => c.name);
  const selectedNames = formik.values.audience || [];
  const isAllSelected = selectedNames.includes("All Customers") || selectedNames.length === allNames.length;
  const someButNotAll = selectedNames.length > 0 && !isAllSelected;

  return (
    <>
      <Dialog open={open} onClose={(_, reason) => reason !== "backdropClick" && onClose()} maxWidth="md" fullWidth>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={dialogStyles.title}>{isEditMode ? "Edit Alert" : "Create Alert"}</DialogTitle>

          <DialogContent sx={dialogStyles.content}>
            <Grid container spacing={3} pt={2}>
              <Grid item xs={6}>
                <TextField
                  label="Alert Title"
                  name="alertTitle"
                  fullWidth
                  value={formik.values.alertTitle}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.alertTitle && !!formik.errors.alertTitle}
                  helperText={formik.touched.alertTitle && formik.errors.alertTitle}
                  InputLabelProps={{ style: dialogStyles.inputLabel }}
                  InputProps={{ style: dialogStyles.input }}
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel sx={dialogStyles.inputLabel}>Severity</InputLabel>
                  <Select
                    name="severity"
                    value={formik.values.severity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    sx={dialogStyles.input}
                  >
                    {severityTypes.map((t) => (
                      <MenuItem key={t.alertSeverityTypeId} value={t.name}>
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel sx={dialogStyles.inputLabel}>Audience</InputLabel>
                  <Select
                    multiple
                    value={formik.values.audience || []}
                    onChange={(e) => {
                      const value = e.target.value as string[];

                      if (value[value.length - 1] === "All Customers") {
                        formik.setFieldValue("audience", ["All Customers"]);
                        return;
                      }

                      if (formik.values.audience.includes("All Customers")) {
                        formik.setFieldValue(
                          "audience",
                          value.filter((v) => v !== "All Customers")
                        );
                        return;
                      }

                      formik.setFieldValue(
                        "audience",
                        value.filter((v) => v !== "All Customers")
                      );
                    }}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) return "";
                      if ((selected as string[]).includes("All Customers")) return "All Customers";
                      return (selected as string[]).join(", ");
                    }}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    <ListSubheader>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Search..."
                        fullWidth
                        value={audienceSearch}
                        onChange={(e) => setAudienceSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                    </ListSubheader>

                    {filteredAudienceCategories.map((category) => {
                      const categoryName = category.name.trim();
                      const selected = (formik.values.audience || []).map((name) => name.trim());

                      const isAllCustomers = categoryName === "All Customers";
                      const hasAllSelected = selected.includes("All Customers");
                      const isSelected = selected.includes(categoryName);

                      const checked = isAllCustomers ? hasAllSelected : hasAllSelected || isSelected;

                      const indeterminate = isAllCustomers && selected.length > 1 && !hasAllSelected;

                      return (
                        <MenuItem key={category.alertAudienceCategoryId} value={categoryName}>
                          <Checkbox checked={checked} indeterminate={indeterminate} />
                          <ListItemText primary={category.name} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel sx={dialogStyles.inputLabel}>Status</InputLabel>
                  <Select name="status" value={formik.values.status} onChange={formik.handleChange} sx={dialogStyles.input}>
                    {sourceTypes.map((s) => (
                      <MenuItem key={s.alertSourceTypeId} value={s.name}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  type="datetime-local"
                  label="Start Date"
                  name="startDate"
                  fullWidth
                  value={formik.values.startDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.startDate && !!formik.errors.startDate}
                  helperText={formik.touched.startDate && formik.errors.startDate}
                  InputLabelProps={{ shrink: true, style: dialogStyles.inputLabel }}
                  InputProps={{ style: dialogStyles.input }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  type="datetime-local"
                  label="End Date"
                  name="endDate"
                  fullWidth
                  value={formik.values.endDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.endDate && !!formik.errors.endDate}
                  helperText={formik.touched.endDate && formik.errors.endDate}
                  InputLabelProps={{ shrink: true, style: dialogStyles.inputLabel }}
                  InputProps={{ style: dialogStyles.input }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#ccc", mb: 1 }}>
                  Description
                </Typography>
                <ReactQuill theme="snow" value={formik.values.description} onChange={(v) => formik.setFieldValue("description", v)} />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={dialogStyles.actions}>
            <Button onClick={() => setShowCancelConfirmation(true)} variant="outlined" color="inherit">
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit">
              {isEditMode ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <AlertConfirmationDialog
        title={isEditMode ? "Update Alert" : "Create Alert"}
        subText={getConfirmationMessage()}
        isVisible={confirmationDialogVisible}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmationDialogVisible(false)}
        isConfirmButtonDisabled={isSaving}
      />

      <ConfirmationDialog
        title="Discard Changes"
        subText="Are you sure you want to discard changes and exit?"
        isVisible={showCancelConfirmation}
        onConfirm={() => {
          setShowCancelConfirmation(false);
          formik.resetForm();
          onClose();
        }}
        onCancel={() => setShowCancelConfirmation(false)}
      />

      <SuccessMessage
        isVisible={successMessageVisible}
        onConfirm={() => setSuccessMessageVisible(false)}
        title={successMessageTitle}
        message="Your changes have been saved."
      />
    </>
  );
};

export default CreateAlert;
